<template>
  <div class="content">
    <div class="container">
      <el-form
        class="form_pad"
        ref="formPad"
        :inline="true"
        :model="listQuery"
      >
      <el-form-item :label="$t('statInfo.timeRange')">
        <DateRangePicker
          v-model="dateRange"
          :compare.sync="compareRange"
          :preset.sync="datePreset"
          :width="302"
        />
      </el-form-item>
      <!-- 操作系统 -->
      <el-form-item :label="$t('common.OS')">
        <MultiSelect
          v-model="listQuery.os_types"
          :options="osOptions"
          :width="160"
        />
      </el-form-item>
      <!-- 应用 -->
      <el-form-item :label="$t('common.app')">
        <MultiSelect
          v-model="listQuery.app_ids"
          :options="appOptions"
          :width="160"
        />
      </el-form-item>
      <!-- 广告位：按所属应用分组，点开应用才是它底下的广告位 -->
      <el-form-item :label="$t('common.adZone')">
        <MultiSelect
          v-model="listQuery.media_ad_slots"
          :groups="adSlotGroups"
          :width="160"
          :panelWidth="640"
        />
      </el-form-item>
      <!-- 广告类型 -->
      <el-form-item :label="$t('sdk.flowType')">
        <MultiSelect
          v-model="listQuery.media_ad_types"
          :options="adTypeOptions"
          :width="140"
        />
      </el-form-item>
      <!-- 合作模型 -->
      <el-form-item :label="$t('common.co_mode')">
        <MultiSelect
          v-model="listQuery.co_modes"
          :options="coModeOptions"
          :width="140"
        />
      </el-form-item>
      <!-- 国家 -->
      <el-form-item :label="$t('common.country')">
        <MultiSelect
          v-model="listQuery.countries"
          :options="countryOptions"
          :width="160"
        />
      </el-form-item>
      <!-- 时区 -->
      <el-form-item :label="$t('statInfo.timezone')">
        <el-select
          v-model="listQuery.use_utc0"
          style="width: 160px"
        >
          <el-option label="UTC+8" :value="false" />
          <el-option label="UTC+0" :value="true" />
        </el-select>
      </el-form-item>
      <!-- 查询按钮 -->
      <el-form-item style="float: right;">
        <el-button type="primary" @click="querySearch">{{$t('statInfo.query')}}</el-button>
      </el-form-item>
      </el-form>
      <!-- 指标预览卡片：种类和顺序由后端保存的配置决定，最多 4 张 -->
      <MetricCards
        :cards="metricCards"
        :metricOptions="metricOptions"
        :canAdd="canAddMetric"
        :showRatio="!!queriedCompare"
        @add="addCard"
        @remove="removeCard"
        @reorder="onCardReorder"
      />
      <!-- 趋势图表：1~6 张，每张独立选指标和对比维度 -->
      <TrendCharts
        :charts="charts"
        :metricOptions="chartMetricOptions"
        :dimensionOptions="dimensionOptions"
        :compareLabels="compareLabels"
        :collapsed.sync="chartCollapsed"
        @add="addChart"
        @remove="removeChart"
        @change="updateChart"
      />
      <div class="downLoad drawer_line clear_fix">
        {{$t('statInfo.summaryReport')}}
        <div class="btn_style" style="float:right">
          <span class="fred">
            {{$t('statInfo.downloadTip')}}
          </span>
          <el-button type="primary" size="mini" @click="exportExcel">
            {{$t('statInfo.export')}}
          </el-button>
        </div>
      </div>
      <div class="report-body">
        <div class="table-container">
          <el-table
            ref="orderTable"
            class="table"
            :data="tableList"
            style="width: 100%;margin-top:12px;"
            v-loading="listLoading"
            :row-class-name="rowClassName"
            :default-sort="defaultSort"
            @sort-change="handleSortChange"
            >
            <!-- 这里的key选择是因为要支持拖拽，要是不带上下标的话，拖动换序时key不变，导致表格无法正确更新 -->
            <el-table-column
              v-for="(col, index) in tableColumns"
              :key="col.key + '-' + index"
              :prop="col.prop"
              :label="col.label"
              :min-width="colWidths[index]"
              :fixed="index === 0 ? 'left' : false"
              align="left"
              sortable="custom"
            >
              <template slot-scope="scope">
                <div class="cell-text">{{ cellText(col, scope.row) }}</div>
              </template>
            </el-table-column>
            <template slot="empty">
              <div class="nodata table-empty">
                <img src="../../../../assets/img/nodata.png" class="nodata_img" alt="">
                <p class="nodata_text">{{ $t('statInfo.noData') }}</p>
              </div>
            </template>
          </el-table>
          <el-pagination
            background
            class="pagination"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
            layout="total, sizes, prev, pager, next"
            :page-size="listQuery.page_size"
            :current-page.sync="listQuery.page"
            :total="total"
          >
          </el-pagination>
        </div>
        <ReportPanel
          class="report-panel"
          :dimensions="panelDims"
          :metrics="panelMetrics"
          @toggle="onPanelToggle"
          @reorder="onPanelReorder"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import APIs from '@/api'
import util from '@/utils/util';
import DateRangePicker from '../../components/DateRangePicker/index.vue'
import MultiSelect from '../../components/MultiSelect/index.vue'
import MetricCards from '../../components/MetricCards/index.vue'
import TrendCharts from '../../components/TrendCharts/index.vue'
import ReportPanel from '../../components/ReportPanel/index.vue'
import { formatMetric, formatCardMetric, metricUnit } from '../../utils/metric'

// 卡片的默认指标
const DEFAULT_CARD_METRICS = ['media_price', 'cpm_price', 'imp']

// 趋势图表的默认指标
const DEFAULT_CHART_METRICS = ['media_price', 'rawreq']
// 新增图表时那张图的初始配置
const NEW_CHART_METRIC = 'media_price'
const MAX_CHARTS = 6

// 汇总报表右侧面板的默认勾选
const DEFAULT_TABLE_DIMS = ['date']
const DEFAULT_TABLE_METRICS = ['rawreq', 'bid', 'imp', 'clk', 'cpm_price', 'media_price']
// 没选日期维度时的默认排序列
const DEFAULT_SORT_METRIC = 'media_price'
// 汇总行的本地标记。维度是可以进行拖拽的，汇总不一定是按照哪个指标，而且这个不会传递到后端，所以需要前端进行标记
const SUMMARY_FLAG = '__isSummary'

// 汇总报表列宽自适应：el-table 是 table-layout: fixed，列宽不会被内容撑开，
// 所以自己按当前页的内容量一遍每列需要多宽，再喂给 min-width。
// 上限是为了别让一个超长的广告位名把整张表撑变形 —— 超出上限的部分交给单元格换行（.cell-text）
const COL_MIN_WIDTH = 80
const COL_MAX_WIDTH = 260
const CELL_PADDING = 24 // .cell 左右各 10px padding，再留 4px 余量
const SORT_CARET_WIDTH = 24 // 表头排序箭头（.caret-wrapper）固定 24px
const TABLE_FONT_SIZE = 14 // 表头表体都是 14px（main.css 的 .el-table thead / .table）
const HEADER_FONT_WEIGHT = 500 // element-ui 的 .el-table thead

let measureCtx = null
let measureFont = ''
// 用 canvas 量文字宽度：不插 DOM 节点、不触发重排。量不出来时返回 0，由调用方退回默认宽度
function textWidth(text, weight) {
  const str = text === 0 ? '0' : String(text || '')
  if (!str) return 0
  if (measureCtx === null) {
    const canvas = document.createElement('canvas')
    measureCtx = (canvas.getContext && canvas.getContext('2d')) || false
    measureFont = window.getComputedStyle(document.body).fontFamily || 'sans-serif'
  }
  if (!measureCtx) return 0
  measureCtx.font = (weight ? weight + ' ' : '') + TABLE_FONT_SIZE + 'px ' + measureFont
  return measureCtx.measureText(str).width
}

// 趋势图的对比维度
const CURVE_DIM_MAP = {
  total: 'total',
  application_id: 'app',
  country: 'country',
  flow_type: 'ad_type',
  os_type: 'os',
  media_ad_slot: 'ad_slot',
  co_mode: 'co_mode'
}

// 查询允许的最大时间跨度
const MAX_SPAN_MONTHS = 3

export default {
  components: { DateRangePicker, MultiSelect, MetricCards, TrendCharts, ReportPanel },
  data() {
    return {
      listLoading: false,
      // 趋势图表区块是否收起
      chartCollapsed: false,
      dateRange: [],
      // 对比区间，由时间选择器通过 .sync 维护；enabled 为 false 时 start/end 为空。
      // 默认开启 + 默认「上一时间段」，start/end 在 created 里按主区间算出来
      compareRange: {
        enabled: true,
        mode: 'prev',
        start: '',
        end: ''
      },
      queriedCompare: null,
      // 时间选择器主面板选中的预设
      datePreset: 'custom',
      queriedPreset: '',
      // 现有表格数据是否为单日（时间别）数据，仅在查询返回后更新，避免改动日期控件就影响已有数据的展示
      queriedSingleDay: true,
      total: 0,
      // 汇总报表右侧面板的已选项
      tableDims: DEFAULT_TABLE_DIMS.slice(),
      tableMetrics: DEFAULT_TABLE_METRICS.slice(),
      // 程序调 $refs.orderTable.sort() 同步表头箭头时会反过来触发 sort-change，
      // 用这个标志位挡掉，否则会多发一次请求
      sortSyncing: false,
      // stats / curve / summary_cards / export 共用的筛选条件。
      listQuery: {
        start_date: '',
        end_date: '',
        os_types: [],
        app_ids: [],
        media_ad_types: [],
        co_modes: [],
        media_ad_slots: [],
        page: 1,
        page_size: 20,
        sort_by: '',
        sort_order: 'desc',
        use_utc0: false,
      },
      tableList: [],
      osList: [],
      appList: [],
      adSlotList: [],
      adTypeList: [],
      coModeList: [],
      countryList: [],
      // 趋势图表
      charts: [],
      // 只用来发 :key 和认领回调，不参与业务
      chartSeq: 0,
      // report_config 原样存着，POST 回去时要连 media_id / curve_metrics 一起带上，
      // 这一轮只动 card_metrics，不能把图表的配置冲掉
      reportConfig: { media_id: 0, curve_metrics: [], card_metrics: [] },
      // summary_cards 的返回，按 key 存成 map 方便查
      cardData: {},
    }
  },
  computed:{
    // 筛选下拉框的选项统一由 toOptions() 归一化成 { value, label }
    osOptions() {
      return this.toOptions(this.osList)
    },
    appOptions() {
      return this.toOptions(this.appList)
    },
    adTypeOptions() {
      return this.toOptions(this.adTypeList, item => this.adTypeName(item.code))
    },
    coModeOptions() {
      return this.toOptions(this.coModeList)
    },
    countryOptions() {
      return this.toOptions(this.countryList, this.labelWithCode)
    },
    adSlotGroups() {
      const groups = []
      this.adSlotList.forEach(item => {
        if (this.isAllSentinel(item)) return
        let group = groups.filter(g => g.value === item.application_id)[0]
        if (!group) {
          group = { value: item.application_id, label: item.application_name, children: [] }
          groups.push(group)
        }
        group.children.push({ value: item.code, label: this.labelWithCode(item) })
      })
      return groups
    },
    // 汇总报表可选的 7 个维度
    dimAll() {
      return [
        // 单日查询时后端按小时分组，名字也跟着变成「小时」
        {
          key: 'date',
          prop: this.dateProp,
          name: this.queriedSingleDay ? this.$t('abroadStat.dimHour') : this.$t('abroadStat.dimDate'),
          minWidth: 90
        },
        { key: 'os_type', prop: 'os_type', name: this.$t('common.OS'), minWidth: 100 },
        { key: 'application_id', prop: 'application', name: this.$t('common.app'), minWidth: 150 },
        { key: 'media_ad_slot', prop: 'media_ad_slot', name: this.$t('common.adZone'), minWidth: 220 },
        { key: 'flow_type', prop: 'flow_type', name: this.$t('sdk.flowType'), minWidth: 120 },
        { key: 'co_mode', prop: 'co_mode', name: this.$t('common.co_mode'), minWidth: 110 },
        { key: 'country', prop: 'country', name: this.$t('common.country'), minWidth: 100 }
      ]
    },
    // 面板里的维度
    panelDims() {
      return this.sortSelectedFirst(this.dimAll, this.tableDims)
    },
    panelMetrics() {
      const all = this.statsList.map(item => ({ key: item.label, name: item.name }))
      return this.sortSelectedFirst(all, this.tableMetrics)
    },
    // 日期列的字段名。单日按小时分组，时间段按照dt进行分组
    dateProp() {
      return this.queriedSingleDay ? 'hr' : 'dt'
    },
    // 分组维度就是面板里勾的维度（日期不算，它由时间范围决定），顺序也跟着面板。
    dataDim() {
      return this.tableDims.filter(key => key !== 'date')
    },
    // 取消勾选「日期」就是不按时间分组，整段时间合成一行
    summarizeFlag() {
      return this.tableDims.indexOf('date') === -1
    },
    // 表格的列。维度在前、指标在后，各自内部按面板里的顺序
    tableColumns() {
      const dims = this.tableDims
        .map(key => this.dimAll.filter(item => item.key === key)[0])
        .filter(item => item)
        .map(item => ({
          key: item.key,
          prop: item.prop,
          label: item.name,
          type: item.key === 'date' ? 'date' : 'dim',
          minWidth: item.minWidth
        }))
      const metrics = this.tableMetrics
        .map(key => this.statsList.filter(item => item.label === key)[0])
        .filter(item => item)
        .map(item => ({
          key: item.label,
          prop: item.label,
          label: item.name,
          minWidth: 120
        }))
      return dims.concat(metrics)
    },
    // 每列真正用的 min-width：按表头 + 当前页单元格的实际内容量出来。
    // 列少时 el-table 会把容器的剩余宽度再按比例分给各列，列多时超出容器就横向滚动。
    // 单独拆一个 computed 而不是塞进 tableColumns：量宽要调 cellText，
    // 而 cellText 处理汇总行时会读 tableColumns[0]，写在一起就成了循环依赖
    colWidths() {
      return this.tableColumns.map(col => this.autoColWidth(col))
    },
    // 给 el-table 的 :default-sort，让表头箭头和 listQuery 对上
    defaultSort() {
      return {
        prop: this.listQuery.sort_by,
        order: this.listQuery.sort_order === 'asc' ? 'ascending' : 'descending'
      }
    },
    statsList() {
      return [
        { label: "rawreq", name: this.$t('statInfo.rawReq') },
        { label: "bid", name: this.$t('statInfo.bid') },
        { label: "bid_rate", name: this.$t('abroadStat.bidRate') },
        { label: "imp", name: this.$t('statInfo.exposure') },
        { label: "imp_rate", name: this.$t('statInfo.exposureRate') },
        { label: "clk", name: this.$t('statInfo.click') },
        { label: "click_rate", name: this.$t('statInfo.clickRate') },
        { label: "cpm_price", name: this.$t('statInfo.CPM') },
        { label: "media_price", name: this.$t('statInfo.profit') },
      ]
    },
    // 卡片按 card_metrics 的顺序排
    metricCards() {
      return this.reportConfig.card_metrics.map(key => {
        const hit = this.statsList.filter(item => item.label === key)[0]
        const row = this.cardData[key] || {}
        return {
          key: key,
          name: this.metricCardName(hit ? hit.name : key),
          text: this.metricText(key, row.value),
          // 原样透传，接口没给就是 null，组件那边会显示成 -；
          // 这一行画不画由 showRatio（即对比期开没开）决定，和这里无关
          ratio: row.ratio === undefined ? null : row.ratio
        }
      })
    },
    // 下拉里列出全部指标，已经加进卡片的置灰
    metricOptions() {
      return this.statsList.map(item => ({
        value: item.label,
        label: item.name,
        disabled: this.reportConfig.card_metrics.indexOf(item.label) !== -1
      }))
    },
    canAddMetric() {
      return this.reportConfig.card_metrics.length < 4
    },
    // 图表的指标下拉
    chartMetricOptions() {
      return this.statsList.map(item => ({
        value: item.label,
        label: item.name
      }))
    },
    // 图表的对比维度下拉
    dimensionOptions() {
      return [
        { value: 'total', label: this.$t('abroadStat.dimTotal') },
        { value: 'application_id', label: this.$t('common.app') },
        { value: 'country', label: this.$t('common.country') },
        { value: 'flow_type', label: this.$t('sdk.flowType') },
        { value: 'os_type', label: this.$t('common.OS') },
        { value: 'media_ad_slot', label: this.$t('common.adZone') },
        { value: 'co_mode', label: this.$t('common.co_mode') }
      ]
    },
    // 对比期每个数据点对应的日期文案
    // 横轴 label 是本期的日期，对比期的日期得自己按天推出来
    compareLabels() {
      if (!this.queriedCompare) return null
      const start = this.queriedCompare.tb_start_date
      const days = this.dayCount(this.listQuery.start_date, this.listQuery.end_date)
      const list = []
      if (days <= 1) {
        // 单日是分小时的，24 个点都落在对比期那一天上
        const text = this.dateText(start)
        for (let i = 0; i < 24; i++) list.push(text)
        return list
      }
      for (let i = 0; i < days; i++) list.push(this.dateText(this.shiftDate(start, i)))
      return list
    }
  },
  created() {
    // 初始化默认当天，模板全部
    this.dateRange = [util.getNowFormatDate(), util.getNowFormatDate()]
    // 对比期默认开启、默认「上一时间段」。
    const cmp = this.prevPeriodRange(this.dateRange[0], this.dateRange[1])
    this.compareRange = { enabled: true, mode: 'prev', start: cmp.start, end: cmp.end }
    this.syncDateParams()
    this.applyDefaultSort()
    this.initCharts()
    this.getStats()
    // 卡片请求要带 card_metrics，得等配置回来，getReportConfig 里会接着调 getSummaryCards
    this.getReportConfig()
    this.getBaseList()
  },
  methods: {
    formatSummary(val, locale) {
      let str = ""
      if(val == "汇总") {
        if(locale == "zh") {
          str = "汇总"
        } else {
          str = "summary"
        }
      } else {
        str = val
      }
      return str
    },
    getBaseList() {
      this.__getData('abroadBaseInfo', null, res => {
        if(res.code == 1) {
          this.$message.error(res.msg);
        } else {
          this.osList = res.os_types || []
          this.appList = res.applications || []
          this.adSlotList = res.ad_slots || []
          this.adTypeList = res.ad_types || []
          this.coModeList = res.co_modes || []
          this.countryList = res.countries || []
        }
      })
    },
    // dim_info 的每项都是 { id, name, code }，统一转成下拉框要的 { value, label }。
    toOptions(list, labelFn) {
      return (list || [])
        .filter(item => !this.isAllSentinel(item))
        .map(item => ({
          value: item.code,
          label: labelFn ? labelFn(item) : item.name
        }))
    },
    // 每个列表开头都有一个全部的选项，要过滤掉，不然和全选冲突
    isAllSentinel(item) {
      return item.code === undefined || item.code === null || Number(item.code) === 0
    },
    // 中文用 dim_info 返回的名字；英文和广告位页面共用 commonList 里的 en_name，两边只维护一份
    adTypeName(code) {
      if (code === undefined || code === null) return ''
      const hit = this.adTypeList.filter(item => Number(item.code) === Number(code))[0]
      const fallback = hit ? hit.name : String(code)
      if (this.$i18n.locale === 'zh') return fallback
      const flowTypes = [...this.$com.abroadList.flowTypeOpenAd, ...this.$com.abroadList.flowTypeList]
      const flow = flowTypes.filter(item => item.id === Number(code))[0]
      return flow ? flow.en_name : fallback
    },
    // 名称【代码】：国家和广告位用
    labelWithCode(item) {
      return item.name + '【' + item.code + '】'
    },
    // 卡片标题带上时间范围：「预估收益（近7天）」
    metricCardName(name) {
      const range = this.cardRangeText()
      if (!range) return name
      return this.$t('abroadStat.metricNamePattern')
        .replace('%s', name)
        .replace('%p', range)
    },
    cardRangeText() {
      if (this.queriedPreset && this.queriedPreset !== 'custom') {
        return this.$t('dateRangePicker.' + this.queriedPreset)
      }
      const start = this.dateText(this.listQuery.start_date)
      const end = this.dateText(this.listQuery.end_date)
      if (!start || !end) return ''
      return start === end ? start : start + '~' + end
    },
    dateText(val) {
      const str = String(val === undefined || val === null ? '' : val)
      if (str.length !== 8) return ''
      return str.substring(0, 4) + '-' + str.substring(4, 6) + '-' + str.substring(6, 8)
    },
    // YYYYMMDD 转 Date。util 里没有日期运算，对比期的逐日推算只在这一页用，就放这儿
    toDate(val) {
      const str = String(val === undefined || val === null ? '' : val)
      if (str.length !== 8) return null
      return new Date(
        Number(str.substring(0, 4)),
        Number(str.substring(4, 6)) - 1,
        Number(str.substring(6, 8))
      )
    },
    // 含首尾的天数：同一天是 1
    dayCount(start, end) {
      const from = this.toDate(start)
      const to = this.toDate(end)
      if (!from || !to) return 0
      return Math.floor((to - from) / 86400000) + 1
    },
    // YYYYMMDD 往后推 n 天，仍返回 YYYYMMDD
    shiftDate(val, days) {
      const date = this.toDate(val)
      if (!date) return ''
      date.setDate(date.getDate() + days)
      const month = date.getMonth() + 1
      const day = date.getDate()
      return String(date.getFullYear()) +
        (month < 10 ? '0' + month : String(month)) +
        (day < 10 ? '0' + day : String(day))
    },
    // YYYYMMDD 转回选择器用的 YYYY-MM-DD
    dashDate(val) {
      const str = String(val)
      if (str.length !== 8) return ''
      return str.substring(0, 4) + '-' + str.substring(4, 6) + '-' + str.substring(6, 8)
    },
    // 「上一时间段」：和主区间等长、紧邻在它之前
    prevPeriodRange(start, end) {
      const from = this.dateSplit(start)
      const len = this.dayCount(from, this.dateSplit(end))
      if (!len) return { start: '', end: '' }
      const cmpEnd = this.shiftDate(from, -1)
      return { start: this.dashDate(this.shiftDate(cmpEnd, -(len - 1))), end: this.dashDate(cmpEnd) }
    },
    // 卡片上的数值文案。计数类要按 K / M / B 缩写
    metricText(key, val) {
      return formatCardMetric(key, val)
    },
    // 已选的按 selected 数组的顺序排在前，未选的按全集顺序排在后
    sortSelectedFirst(all, selected) {
      const on = selected
        .map(key => all.filter(item => item.key === key)[0])
        .filter(item => item)
        .map(item => ({ key: item.key, name: item.name, selected: true }))
      const off = all
        .filter(item => selected.indexOf(item.key) === -1)
        .map(item => ({ key: item.key, name: item.name, selected: false }))
      return on.concat(off)
    },
    // 勾选 / 取消勾选
    onPanelToggle(type, key) {
      const list = type === 'dim' ? this.tableDims : this.tableMetrics
      const has = list.indexOf(key) !== -1
      if (type === 'metric' && has && list.length <= 1) {
        this.$message.warning(this.$t('abroadStat.metricAtLeastOne'))
        return
      }
      const next = has ? list.filter(item => item !== key) : list.concat(key)
      if (type === 'dim') {
        this.tableDims = next
      } else {
        this.tableMetrics = next
      }
      // 排序列被取消展示了才重置，其他列的增删不影响排序
      const sortChanged = this.ensureSort()
      // 指标列的增删只改「显示哪几列」：stats 请求里没有指标清单，后端一直是全量字段返回，
      // 行数据本来就带着那些值，不用重新取数。
      // 维度变了要重取（分组口径变了，行本身就不一样了）；
      // 排序列被取消、排序被迫回默认也要重取（行序变了，而且分页在后端）
      if (type === 'dim' || sortChanged) {
        this.listQuery.page = 1
        this.getStats()
      }
    },
    // 拖拽换顺序。往下拖是插到目标之后、往上拖是插到目标之前
    onPanelReorder(type, fromKey, toKey) {
      const list = (type === 'dim' ? this.tableDims : this.tableMetrics).slice()
      const from = list.indexOf(fromKey)
      const to = list.indexOf(toKey)
      if (from === -1 || to === -1 || from === to) return
      list.splice(from, 1)
      list.splice(to, 0, fromKey)
      if (type === 'dim') {
        this.tableDims = list
      } else {
        this.tableMetrics = list
      }
      // 换了位的列会被销毁重建（见模板里 key 的注释），带来两件收尾：
      // 1. 排序中的那一列如果被重建了，store 里记的还是旧的 column 对象，表头箭头会丢，
      //    重新同步一次（listQuery 里的排序本身没变，也不会因此重新取数）
      // 2. 换到第一位的列会变成固定列，el-table 的固定层是另画的一张表，
      //    列变了得让它重新量一次宽度，不然固定层和主表会错位
      this.syncSortIndicator()
      this.$nextTick(() => {
        if (this.$refs.orderTable) this.$refs.orderTable.doLayout()
      })
    },
    // 默认排序：勾了日期就按日期倒序，没勾就按预估收益倒序
    applyDefaultSort() {
      let by = ''
      if (this.tableDims.indexOf('date') !== -1) {
        by = this.dateProp
      } else if (this.tableMetrics.indexOf(DEFAULT_SORT_METRIC) !== -1) {
        by = DEFAULT_SORT_METRIC
      } else {
        // 预估收益也没勾，就按 metrics 面板里第一项倒序
        by = this.tableMetrics[0] || ''
      }
      this.setSort(by, 'desc')
    },
    // 排序列还在展示就保留，被取消展示了才回到默认。返回排序有没有被改掉
    ensureSort() {
      const props = this.tableColumns.map(col => col.prop)
      if (this.listQuery.sort_by && props.indexOf(this.listQuery.sort_by) !== -1) return false
      this.applyDefaultSort()
      return true
    },
    setSort(by, order) {
      this.listQuery.sort_by = by
      this.listQuery.sort_order = order
      this.syncSortIndicator()
    },
    syncSortIndicator() {
      this.$nextTick(() => {
        const table = this.$refs.orderTable
        if (!table || !this.listQuery.sort_by) return
        this.sortSyncing = true
        try {
          table.sort(this.listQuery.sort_by, this.defaultSort.order)
        } finally {
          this.sortSyncing = false
        }
      })
    },
    handleSortChange(arg) {
      if (this.sortSyncing) return
      const order = arg ? arg.order : null
      // 第三次点击表头会取消排序，这时回到默认排序而不是不排
      if (!order) {
        this.applyDefaultSort()
      } else {
        this.listQuery.sort_by = arg.prop
        this.listQuery.sort_order = order === 'ascending' ? 'asc' : 'desc'
      }
      this.listQuery.page = 1
      this.getStats()
    },
    // 汇总行
    isSummaryRow(row) {
      return !!(row && row[SUMMARY_FLAG])
    },
    rowClassName(arg) {
      return this.isSummaryRow(arg.row) ? 'is-summary-row' : ''
    },
    autoColWidth(col) {
      const labelWidth = textWidth(col.label, HEADER_FONT_WEIGHT)
      if (!labelWidth) return col.minWidth
      let need = labelWidth + SORT_CARET_WIDTH
      this.tableList.forEach(row => {
        const width = textWidth(this.cellText(col, row))
        if (width > need) need = width
      })
      need = Math.ceil(need) + CELL_PADDING
      return Math.min(Math.max(need, COL_MIN_WIDTH), COL_MAX_WIDTH)
    },
    cellText(col, row) {
      const val = row[col.prop]
      // 汇总行的标记文案要按语言翻，普通维度值原样显示
      if (col.type === 'date' || col.type === 'dim') {
        if (this.isSummaryRow(row)) return this.summaryCellText(col, val)
        if (col.key === 'flow_type') {
          return this.adTypeName(val)
        }
        return this.formatSummary(val, this.$i18n.locale)
      }
      return formatMetric(col.key, val)
    },
    // 汇总行的维度列
    summaryCellText(col, val) {
      if (val !== undefined && val !== null && val !== '') {
        return this.formatSummary(val, this.$i18n.locale)
      }
      const first = this.tableColumns[0]
      if (first && first.key === col.key) {
        return this.formatSummary('汇总', this.$i18n.locale)
      }
      return '-'
    },
    buildFilterParams() {
      const q = this.listQuery
      return {
        start_date: q.start_date,
        end_date: q.end_date,
        // 对比期：开了时间对比就一起带上，没开时 queriedCompare 是 null，
        ...this.queriedCompare,
        use_utc0: q.use_utc0,
        os_types: q.os_types,
        app_ids: q.app_ids,
        media_ad_slots: q.media_ad_slots,
        media_ad_types: q.media_ad_types,
        co_modes: q.co_modes,
        countries: q.countries
      }
    },
    // 明细表格
    getStats() {
      this.listLoading = true
      const params = {
        ...this.buildFilterParams(),
        data_dim: this.dataDim,
        summarize: this.summarizeFlag,
        sort_by: this.listQuery.sort_by,
        sort_order: this.listQuery.sort_order,
        page: this.listQuery.page,
        page_size: this.listQuery.page_size
      }
      this.__postData('abroadStats', params, res => {
        this.listLoading = false
        if(res.code == 1) {
          this.$message.error(res.msg);
        } else {
          const rows = res.data || []
          const sum = res.summary ? Object.assign({}, res.summary, { [SUMMARY_FLAG]: true }) : null
          this.tableList = sum ? [sum].concat(rows) : rows
          this.total = res.pageinfo ? res.pageinfo.total_size : 0
          this.queriedSingleDay = this.listQuery.start_date === this.listQuery.end_date

          this.$nextTick(() => {
            this.$refs.orderTable.doLayout();
          });
          this.syncSortIndicator()
        }
      }).catch(() => {
        this.listLoading = false
      })
    },
    // 趋势曲线
    initCharts() {
      this.charts = DEFAULT_CHART_METRICS.map(key => this.makeChart(key))
      this.fetchAllCharts()
    },
    makeChart(indicator) {
      this.chartSeq += 1
      return {
        id: this.chartSeq,
        indicator: indicator,
        dimension: 'total',
        loading: false,
        token: 0,
        data: null
      }
    },
    fetchChart(chart) {
      const token = chart.token + 1
      chart.token = token
      chart.loading = true
      const params = {
        ...this.buildFilterParams(),
        indicator: chart.indicator,
        dimension: CURVE_DIM_MAP[chart.dimension] || chart.dimension
      }
      this.__postData('abroadCurve', params, res => {
        // 回调期间这张图可能已经被删掉、或者又改了一次配置
        if (this.charts.indexOf(chart) === -1 || chart.token !== token) return
        chart.loading = false
        if(res.code == 1) {
          this.$message.error(res.msg);
        } else {
          chart.data = res
        }
      }).catch(() => {
        if (this.charts.indexOf(chart) === -1 || chart.token !== token) return
        chart.loading = false
      })
    },
    fetchAllCharts() {
      this.charts.forEach(chart => this.fetchChart(chart))
    },
    addChart() {
      if (this.charts.length >= MAX_CHARTS) return
      const chart = this.makeChart(NEW_CHART_METRIC)
      this.charts = this.charts.concat(chart)
      this.fetchChart(chart)
    },
    removeChart(id) {
      if (this.charts.length <= 1) return
      this.charts = this.charts.filter(item => item.id !== id)
    },
    // 改指标或维度：只重取这一张
    updateChart(id, patch) {
      const chart = this.charts.filter(item => item.id === id)[0]
      if (!chart) return
      if (patch.indicator !== undefined) chart.indicator = patch.indicator
      if (patch.dimension !== undefined) chart.dimension = patch.dimension
      // 为了本期对比期不会出现错位，改指标或维度时要清掉旧数据，等新数据回来再渲染
      chart.data = null
      this.fetchChart(chart)
    },
    // 指标预览卡片
    getSummaryCards() {
      const params = {
        ...this.buildFilterParams(),
        card_metrics: this.reportConfig.card_metrics
      }
      this.queriedPreset = this.datePreset
      this.__postData('abroadSummaryCards', params, res => {
        if(res.code == 1) {
          this.$message.error(res.msg);
        } else {
          const map = {}
          ;(res.cards || []).forEach(item => {
            map[item.key] = item
          })
          this.cardData = map
        }
      })
    },
    // 卡片配置。整个对象存下来，POST 时原样带回去，只改 card_metrics
    getReportConfig() {
      this.__getData('abroadReportConfig', null, res => {
        if(res.code == 1) {
          this.$message.error(res.msg);
          this.reportConfig = { media_id: 0, curve_metrics: [], card_metrics: DEFAULT_CARD_METRICS.slice() }
        } else {
          this.reportConfig = {
            media_id: res.media_id || 0,
            curve_metrics: res.curve_metrics || [],
            card_metrics: Array.isArray(res.card_metrics)
              ? res.card_metrics
              : DEFAULT_CARD_METRICS.slice()
          }
        }
        this.getSummaryCards()
      })
    },
    saveReportConfig(needCardData) {
      this.__postData('abroadReportConfig', this.reportConfig, res => {
        if(res && res.code == 1) {
          this.$message.error(res.msg);
          return
        }
        if (needCardData) this.getSummaryCards()
      })
    },
    addCard(key) {
      if (this.reportConfig.card_metrics.indexOf(key) !== -1) return
      this.reportConfig.card_metrics = this.reportConfig.card_metrics.concat(key)
      this.saveReportConfig(true)
    },
    removeCard(key) {
      this.reportConfig.card_metrics = this.reportConfig.card_metrics.filter(item => item !== key)
      this.saveReportConfig()
    },
    // 拖拽换卡片顺序
    onCardReorder(fromKey, toKey) {
      const list = this.reportConfig.card_metrics.slice()
      const from = list.indexOf(fromKey)
      const to = list.indexOf(toKey)
      if (from === -1 || to === -1 || from === to) return
      list.splice(from, 1)
      list.splice(to, 0, fromKey)
      this.reportConfig.card_metrics = list
      this.saveReportConfig()
    },
    // 查询前校验时间跨度，超了 toast 提示并拦住请求。
    checkDateSpan() {
      const range = this.dateRange || []
      if (!range[0] || !range[1]) return true
      const max = this.toDate(this.dateSplit(range[0]))
      const end = this.toDate(this.dateSplit(range[1]))
      if (!max || !end) return true
      max.setMonth(max.getMonth() + MAX_SPAN_MONTHS)
      max.setDate(max.getDate() - 1)
      if (end <= max) return true
      this.$message.warning(this.$t('abroadStat.dateSpanLimit'))
      return false
    },
    querySearch() {
      if (!this.checkDateSpan()) return
      this.syncDateParams()
      this.listQuery.page = 1
      this.getStats()
      this.fetchAllCharts()
      this.getSummaryCards()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.getStats()
    },
    handleSizeChange(val) {
      this.listQuery.page = 1
      this.listQuery.page_size = val
      this.getStats()
    },
    // 日期处理
    dateSplit(val){
      let str = ""
      if(val.substring(8,10).length == 1) {
        str = '0' + val.substring(8,10)
      } else {
        str = val.substring(8,10)
      }
      return val.substring(0,4) + val.substring(5,7) + str
    },
    // 把时间选择器的选择同步成接口参数
    syncDateParams() {
      if (this.dateRange.length > 0) {
        this.listQuery.start_date = Number(this.dateSplit(this.dateRange[0]))
        this.listQuery.end_date = Number(this.dateSplit(this.dateRange[1]))
      }
      // 单日查询后端按小时分组，日期列的字段名是 hr 而不是 dt
      if (this.listQuery.sort_by === 'dt' || this.listQuery.sort_by === 'hr') {
        this.listQuery.sort_by = this.listQuery.start_date === this.listQuery.end_date ? 'hr' : 'dt'
      }
      this.queriedCompare = this.buildCompareDateParams()
    },
    buildCompareDateParams() {
      if (!this.compareRange.enabled || !this.compareRange.start || !this.compareRange.end) {
        return null
      }
      return {
        tb_start_date: Number(this.dateSplit(this.compareRange.start)),
        tb_end_date: Number(this.dateSplit(this.compareRange.end))
      }
    },
    exportColumns() {
      return this.tableColumns.map(col => col.prop)
    },
    exportExcel() {
      const params = {
        ...this.buildFilterParams(),
        data_dim: this.dataDim,
        summarize: this.summarizeFlag,
        sort_by: this.listQuery.sort_by,
        sort_order: this.listQuery.sort_order,
        columns: this.exportColumns()
      }
      this.listLoading = true
      axios({
        method: 'post',
        url: APIs.abroadStatsExport.replace('/api', '/abroadApi'),
        data: params,
        responseType: 'blob',
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: util.getCookie('token')
        }
      }).then(res => {
        this.listLoading = false
        // 失败时后端仍然回 JSON 包裹，但这里收到的是 blob，得先读成文本才拿得到 msg
        if (String(res.headers['content-type'] || '').indexOf('json') !== -1) {
          this.showBlobError(res.data)
          return
        }
        this.saveBlob(res.data, this.exportFileName(res.headers['content-disposition']))
      }).catch(() => {
        this.listLoading = false
        this.$message.error(this.$t('abroadStat.exportFail'))
      })
    },
    exportFileName(disposition) {
      const hit = /filename=([^;]+)/.exec(disposition || '')
      return hit ? hit[1].trim().replace(/^"|"$/g, '') : 'report_v3.xlsx'
    },
    showBlobError(blob) {
      const reader = new FileReader()
      reader.onload = () => {
        let msg = ''
        try {
          msg = JSON.parse(reader.result).msg
        } catch (e) {
          msg = ''
        }
        this.$message.error(msg || this.$t('abroadStat.exportFail'))
      }
      reader.readAsText(blob)
    },
    saveBlob(blob, filename) {
      const href = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = href
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(href)
    }
  }
}
</script>
<style  lang="scss" scoped>
.exportIcon {
  width: 14px;
  height: 14px;
}
.downLoad {
  padding-top: 10px;
}

.report-body {
  clear: both;
  display: flex;
  align-items: flex-start;
  width: 100%;
  box-sizing: border-box;
}
.report-panel {
  flex: 0 0 180px;
  margin: 12px 0 0 16px;
}

.table-container {
  flex: 1;
  min-width: 0;
}

.pagination {
  text-align: center;
}

/* 列宽放不下时换行展示全部内容，不用省略号截断。
   word-break 用 normal + overflow-wrap: break-word（而不是 element-ui 默认的 break-all）：
   中文本来就能逐字换行，英文优先在空格处断，只有整个词都放不下时才硬断，
   这样 $6,139.28、2025-09-16 这类不该被切开的值不会中途换行 */
/deep/ .el-table th .cell {
  white-space: normal;
  word-break: normal;
  overflow-wrap: break-word;
}
.cell-text {
  white-space: normal;
  word-break: normal;
  overflow-wrap: break-word;
}

/deep/ .el-table__body tr.is-summary-row > td,
/deep/ .el-table__body tr.is-summary-row:hover > td {
  font-weight: 700;
  color: #1f2329;
  background: #f7f8fa;
}

/deep/ .el-table__empty-text {
  width: 100%;
  line-height: normal;
}
.table-empty {
  padding: 88px 0;
}

.fred {
  color: #E6A23C;
}
</style>
